import {
    DAuditInfo,
    IAuditStamps,
    RhDateTimeInput,
    RhFormWrapper,
    RhTabsWrapper,
    RHTextField,
} from '../../../../../dum';
import React, { useMemo } from 'react';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import {
    ActivityEntityName,
    clearManagement,
    useUpsertTransferDialogMutation,
} from '../../../../../../services';
import { useHistorialTab } from '../../../../historial';
import {
    FIELD_LABELS,
    getDateFromString,
    getDateTimeFromString,
    getStringFromDate,
    getStringFromDateTime,
    shortLabel,
    transformToNumber,
} from '../../../../../../helpers';
import { ToAccountInput } from './to-account/to-account-input';
import { FromAccountInput } from './from-account/from-account-input';
import { TransferReceiptColumns } from './transfer-receipt-columns/transfer-receipt-columns';
import { TransferTypeInput } from './transfer-type/transfer-type-input';
import { AmountInput } from './amount/amount';
import { TransferUtilities } from './utilities/transfer-utilities';
import {
    FinalAmountChangeWarning,
    useFinalAmountChangeConfirmation,
} from '../../use-final-amount-change-confirmation';

export interface ITransfer {
    id?: number | null;
    amount: number;
    transfer_type_id?: number | null;
    from_account?: {
        id?: number | null;
        name: string;
        is_client?: boolean;
        is_own?: boolean;
        is_supplier?: boolean;
        // Informal-money account (Inopack Notas). Paired with the linked
        // document's receipt-type formality by the transfer rule.
        is_informal_account?: boolean | null;
    } | null;
    to_account?: {
        id?: number | null;
        name: string;
        is_client?: boolean;
        is_own?: boolean;
        is_supplier?: boolean;
        is_informal_account?: boolean | null;
    } | null;
    transferred: boolean;
    transferred_date?: string | null;
    expected_date?: string | null;
    notes?: string | null;
    transfer_receipts: {
        id?: number | null;
        order_sale?: {
            id?: number | null;
            // Net of Devolución adjustments — what the client still owes.
            net_total_with_tax?: number | null;
            // Sale folio parts for the copy utilities: prefer the invoice code,
            // fall back to the order code. (compound_order_code stays for labels.)
            compound_order_code?: string | null;
            invoice_code?: number | null;
            order_code?: number | null;
            // Receipt-type formality, for the informal/formal transfer rule.
            receipt_type?: {
                is_informal_receipt?: boolean | null;
            } | null;
        } | null;
        expense?: {
            id?: number | null;
            total_with_tax?: number | null;
            // Expense's external code, for the Utilidades copy buttons.
            external_code?: string | null;
            receipt_type?: {
                is_informal_receipt?: boolean | null;
            } | null;
        } | null;
        total?: number | null;
        amount: number;
        // The linked document's remaining balance at the moment it was picked.
        // Purely a UI hint (offered via "Aplicar" under the amount field); it is
        // never persisted — the mutation maps only id/order_sale_id/expense_id/amount.
        suggested_amount?: number | null;
        // Snapshot of this receipt as the form was first initialized. Lets the
        // pickers restore the original amount when the user re-selects the
        // document the receipt was loaded with. Non-persisted (UI only).
        initial_amount?: number | null;
        initial_order_sale_id?: number | null;
        initial_expense_id?: number | null;
    }[];
}

export const TO_ACCOUNT: keyof ITransfer = 'to_account';
export const TRANSFER_TYPE_ID: keyof ITransfer = 'transfer_type_id';
export const FROM_ACCOUNT: keyof ITransfer = 'from_account';
export const TRANSFER_RECEIPTS: keyof ITransfer = 'transfer_receipts';

interface ITransferForm {
    // Audit stamps ride alongside the form values — display only, never
    // submitted, and deliberately kept out of ITransfer (see IAuditStamps).
    transfer?: (ITransfer & IAuditStamps) | null;
    onSuccess: () => void;
    disabled?: boolean;
}

export function TransferForm({
    transfer,
    onSuccess,
    disabled = false,
}: ITransferForm) {
    const [mutation, { loading }] = useUpsertTransferDialogMutation();

    const historialTab = useHistorialTab({
        entityName: ActivityEntityName.Transfer,
        entityId: transfer?.id,
        unsavedMessage:
            'El historial aparece una vez que la transferencia ha sido guardada.',
        emptyMessage: 'No hay actividades registradas para esta transferencia.',
    });

    const { confirmFinalAmountChange, finalAmountChangeConfirmation } =
        useFinalAmountChangeConfirmation({
            initialAmount: transfer?.amount ?? 0,
            isEditing: !!transfer?.id,
            subject: 'El total transferido',
        });

    const initialValues = useMemo<ITransfer>(() => {
        return {
            id: transfer?.id || null,
            amount: transfer?.amount || 0,
            from_account: transfer?.from_account || null,
            transfer_type_id: transfer?.transfer_type_id || null,
            to_account: transfer?.to_account || null,
            notes: transfer?.notes || '',
            transferred_date:
                transfer && transfer.transferred_date
                    ? getDateTimeFromString(transfer.transferred_date)
                    : null,
            expected_date:
                transfer && transfer.expected_date
                    ? getDateFromString(transfer.expected_date)
                    : null,
            transferred: transfer?.transferred || false,
            transfer_receipts: transfer?.transfer_receipts.map((tr) => {
                return {
                    // Kept so the submit mapping can forward it: without it
                    // every receipt was deleted and recreated on each save.
                    id: tr.id ?? null,
                    expense: tr.expense || null,
                    order_sale: tr.order_sale || null,
                    total: tr.order_sale
                        ? tr.order_sale.net_total_with_tax
                        : tr.expense
                        ? tr.expense.total_with_tax
                        : 0,
                    amount: tr.amount,
                    // Not reconstructed on load (the dialog query doesn't fetch
                    // each document's transfer_receipts_total). It fills in the
                    // moment the user picks/swaps a document.
                    suggested_amount: null,
                    // Frozen snapshot of the loaded values for "restore original".
                    initial_amount: tr.amount,
                    initial_order_sale_id: tr.order_sale?.id ?? null,
                    initial_expense_id: tr.expense?.id ?? null,
                };
            }) || [
                {
                    order_sale_id: null,
                    expense_id: null,
                    amount: 0,
                    total: 0,
                    suggested_amount: null,
                    initial_amount: null,
                    initial_order_sale_id: null,
                    initial_expense_id: null,
                },
            ],
        };
    }, [transfer]);

    const validationSchema: SchemaOf<Omit<ITransfer, ''>> = yup.object({
        id: yup.number().notRequired().nullable(),
        transfer_type_id: yup.mixed().nullable().required(),
        amount: yup.number().transform(transformToNumber).nullable().required(),
        // Own (1), client (2) and supplier (3) transfers require both accounts
        // (server: "from/to account has to be selected"). Adjustment (4) requires
        // exactly one — handled on to_account so the two account fields don't both
        // raise the same message.
        from_account: yup
            .mixed()
            .nullable()
            .notRequired()
            .test(
                'from-account-required',
                'Selecciona la cuenta de retiro',
                function (value: any) {
                    const type = Number(
                        (this.parent as ITransfer).transfer_type_id,
                    );
                    return !([1, 2, 3].includes(type) && !value?.id);
                },
            ),
        to_account: yup
            .mixed()
            .nullable()
            .notRequired()
            .test(
                'to-account-selection',
                'Selecciona la cuenta de abono',
                function (value: any) {
                    const parent = this.parent as ITransfer;
                    const type = Number(parent.transfer_type_id);
                    const toSet = !!value?.id;
                    const fromSet = !!parent.from_account?.id;

                    if ([1, 2, 3].includes(type) && !toSet) {
                        return this.createError({
                            message: 'Selecciona la cuenta de abono',
                        });
                    }
                    // Adjustment: exactly one account (server: "one account has to
                    // be deselected", plus at least one account overall).
                    if (type === 4) {
                        if (toSet && fromSet) {
                            return this.createError({
                                message:
                                    'Ajuste: deja seleccionada solo una cuenta',
                            });
                        }
                        if (!toSet && !fromSet) {
                            return this.createError({
                                message: 'Ajuste: selecciona una cuenta',
                            });
                        }
                    }
                    return true;
                },
            ),
        transferred_date: yup.string().nullable().required(),
        notes: yup.string().nullable().notRequired(),
        expected_date: yup.string().nullable(),
        transferred: yup.boolean().required(),
        transfer_receipts: yup
            .array()
            .of(
                yup.object().shape({
                    id: yup.number().notRequired().nullable(),
                    expense_id: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .notRequired(),
                    order_sale: yup.mixed().nullable().notRequired(),
                    expense: yup.mixed().nullable().notRequired(),
                    total: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .notRequired(),
                    suggested_amount: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .notRequired(),
                    initial_amount: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .notRequired(),
                    initial_order_sale_id: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .notRequired(),
                    initial_expense_id: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .notRequired(),
                    // Intentionally uncapped: a receipt may exceed its document's
                    // total (overpayment). The resulting negative saldo surfaces
                    // in Saldos y pagos and on the sale/expense.
                    amount: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(),
                }),
            )
            .required('Required')
            // Backstop for the informal/formal money rule (the picker already
            // filters options): a participating OWN account may only link to
            // documents whose receipt type matches its formality — informal
            // account ↔ nota receipt, formal account ↔ fiscal receipt. Compared on
            // the two declared flags; a document with no receipt type is exempt.
            .test(
                'receipt-type-matches-account-formality',
                'La formalidad de la cuenta no coincide con el tipo de comprobante del documento (informal ↔ nota, formal ↔ fiscal)',
                function (receipts) {
                    const parent = this.parent as ITransfer;
                    const ownAccount = [
                        parent.from_account,
                        parent.to_account,
                    ].find((account) => account?.is_own);

                    if (
                        !ownAccount ||
                        typeof ownAccount.is_informal_account !== 'boolean'
                    ) {
                        return true;
                    }
                    if (!Array.isArray(receipts)) {
                        return true;
                    }

                    const hasMismatch = receipts.some((receipt) => {
                        const doc = receipt?.order_sale || receipt?.expense;
                        const isInformalReceipt =
                            doc?.receipt_type?.is_informal_receipt;
                        if (typeof isInformalReceipt !== 'boolean') {
                            // No receipt type on the document → unrestricted.
                            return false;
                        }
                        return (
                            isInformalReceipt !==
                            ownAccount.is_informal_account
                        );
                    });

                    return !hasMismatch;
                },
            )
            // A sale may appear on at most one receipt row (server: "order sale is
            // not unique").
            .test(
                'order-sale-unique',
                'Una venta no puede repetirse en dos renglones',
                function (receipts) {
                    if (!Array.isArray(receipts)) return true;
                    const ids = receipts
                        .map((receipt) => receipt?.order_sale?.id)
                        .filter((id): id is number => id != null);
                    return new Set(ids).size === ids.length;
                },
            )
            // An expense may appear on at most one receipt row (server: "expense
            // is not unique").
            .test(
                'expense-unique',
                'Un gasto no puede repetirse en dos renglones',
                function (receipts) {
                    if (!Array.isArray(receipts)) return true;
                    const ids = receipts
                        .map((receipt) => receipt?.expense?.id)
                        .filter((id): id is number => id != null);
                    return new Set(ids).size === ids.length;
                },
            )
            // Each receipt row must carry its document: a sale on a client payment
            // (2), an expense on a supplier payment (3). Mirrors the server's
            // per-row "order sale / expense id has to be defined".
            .test(
                'receipt-document-required',
                'Cada renglón debe tener un documento seleccionado',
                function (receipts) {
                    const type = Number(
                        (this.parent as ITransfer).transfer_type_id,
                    );
                    if (!Array.isArray(receipts)) return true;
                    if (
                        type === 2 &&
                        receipts.some((receipt) => !receipt?.order_sale?.id)
                    ) {
                        return this.createError({
                            message:
                                'Cada renglón debe tener una venta seleccionada',
                        });
                    }
                    if (
                        type === 3 &&
                        receipts.some((receipt) => !receipt?.expense?.id)
                    ) {
                        return this.createError({
                            message:
                                'Cada renglón debe tener un gasto seleccionado',
                        });
                    }
                    return true;
                },
            ),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading || disabled}
            actionBarStart={
                transfer?.id ? (
                    <DAuditInfo
                        created_by={transfer.created_by}
                        updated_by={transfer.updated_by}
                        createdLabel={'Creado por'}
                        updatedLabel={'Actualizado por'}
                    />
                ) : null
            }
            onSubmit={async (data) => {
                const confirmed = await confirmFinalAmountChange(
                    data.amount,
                    [2, 3, 4].includes(Number(data.transfer_type_id)),
                );
                if (!confirmed) return;

                try {
                    await mutation({
                        // A transfer changes what each linked sale/expense still
                        // has pending, so the sale/expense lists, Saldos y pagos,
                        // the account ledger and the own-account summaries must
                        // re-read their backend-recomputed transfer totals.
                        // clearManagement evicts those cached query fields (it also
                        // runs clearSalesModule), so the next read refetches from
                        // the network instead of serving the pre-save value. This
                        // is the piece that was missing on receipt REMOVAL: an
                        // added/updated receipt's document refreshed through other
                        // reads, but a removed one was never re-read and kept its
                        // stale "pendiente por transferir" state.
                        update: (cache) => {
                            clearManagement(cache);
                        },
                        variables: {
                            TransferUpsertInput: {
                                id: transfer?.id || null,
                                amount: data?.amount || 0,
                                transfer_type_id:
                                    data?.transfer_type_id || null,
                                to_account_id: data?.to_account?.id || null,
                                from_account_id: data?.from_account?.id || null,
                                transferred_date: data.transferred_date
                                    ? getStringFromDateTime(
                                          data?.transferred_date,
                                      )
                                    : null,
                                expected_date: data.expected_date
                                    ? getStringFromDate(data?.expected_date)
                                    : null,
                                // A transfer here always carries a (required)
                                // transferred_date, so it is by definition a
                                // completed transfer. Derive the flag from that
                                // instead of always persisting `false`, which
                                // left the stored row self-contradictory.
                                transferred: !!data.transferred_date,
                                notes: data.notes || '',
                                transfer_receipts: data?.transfer_receipts.map(
                                    (tr) => {
                                        return {
                                            id: tr.id || null,
                                            order_sale_id:
                                                tr.order_sale?.id || null,
                                            expense_id: tr.expense?.id || null,
                                            amount: tr.amount || 0,
                                        };
                                    },
                                ),
                            },
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
            onInvalid={async (errors) => {
                console.error(errors);
            }}
        >
            <RhTabsWrapper
                tabs={[
                    {
                        label: 'Transferencia',
                        fields: [
                            TRANSFER_TYPE_ID,
                            FROM_ACCOUNT,
                            TO_ACCOUNT,
                            'transferred_date',
                            TRANSFER_RECEIPTS,
                            'amount',
                            'notes',
                        ],
                        content: (
                            <>
                                {/* Type is the master switch (it re-queries
                                    accounts and reconfigures the receipt grid),
                                    so it leads the form. */}
                                <TransferTypeInput disabled={!!transfer?.id} />
                                <FromAccountInput />
                                <ToAccountInput />
                                <RhDateTimeInput
                                    name={'transferred_date'}
                                    label={shortLabel(
                                        FIELD_LABELS.transferred_date,
                                    )}
                                />
                                <TransferReceiptColumns />
                                <AmountInput />
                                <FinalAmountChangeWarning
                                    amountFieldName={'amount'}
                                    initialAmount={transfer?.amount ?? 0}
                                    isEditing={!!transfer?.id}
                                    subject={'El total transferido'}
                                    enabledFieldName={TRANSFER_TYPE_ID}
                                    enabledValues={[2, 3, 4]}
                                />
                                <RHTextField
                                    name={'notes'}
                                    label={shortLabel(FIELD_LABELS.notes)}
                                    rows={3}
                                />
                            </>
                        ),
                    },
                    {
                        label: 'Herramientas',
                        optional: true,
                        fields: [],
                        content: <TransferUtilities />,
                    },
                    // Empty for non-admins, so they get no tab at all.
                    ...historialTab,
                ]}
            />
            {finalAmountChangeConfirmation}
        </RhFormWrapper>
    );
}
